import { useCallback } from 'react';
import { WorkItemTransitionErrorType, WorkItemTransitionsInputDataType, WorkItemTransitionTriggerType } from '../types';
import { getWorkItem } from 'src/azure/services/work-items';
import { getAzureProcessesService } from 'src/services/azure-devops-api/get-azure-processes-service';
import { getAzureStateDefinitionsService } from 'src/services/azure-devops-api/get-azure-state-definitions-service';
import { arrayIsEmpty } from '../../utils/validations';

export const initialWorkItemTransitions: WorkItemTransitionTriggerType = {
	selectedFrom: [],
	selectedTo: [],
	loading: true,
};

const staticStateOptions = [
	{ value: 'any', label: 'Any' }
];

export default function useWorkItemTransition() {
	const getTransitionOptions = useCallback(async (input: {
    projectId: string;
    organizationName: string;
  }) => {
		const workItemTypeList = await getWorkItem(input.projectId);
		const processesList = await getAzureProcessesService({
			organizationName:  input.organizationName,
			projectId: input.projectId
		});
  
		if (processesList) {
			const resultPromises = await Promise.all(
				workItemTypeList.map((wit) => {
					return Promise.all(processesList.map(async (processItem) => {
						try {
							const stateDefinition = await getAzureStateDefinitionsService({
								organizationName: input.organizationName,
								processId: processItem.value,
								workItemType: wit.referenceName
							});
  
							return stateDefinition?.map((stateDefinitionResponse) => stateDefinitionResponse);
						} catch {
							return null;
						}
					}));
				}));
  
			const stateResults = resultPromises
				.map(process => process)
				.flatMap(items => items)
				.flatMap(items => items)
				.flatMap(items => items)
				.flatMap(item => item)
				.filter((item, idx, selfArr) => {
					return item && selfArr.findIndex(fItem =>  fItem?.id === item.id) === idx;
				});
  
			const results = stateResults.map((item) => {
				return {
					label: item?.name || '',
					value: item?.name || ''
				};
			});
  
			return [...staticStateOptions, ...results];
		}
  
		return [...staticStateOptions];
	}, []);

	const validate = useCallback((values: {
    selectedFrom: Array<string>;
    selectedTo: Array<string>;
  }): {
    hasErrors: () => boolean;
    errors: WorkItemTransitionErrorType
  } => {
		let hasErrors = false;
		const validations = {
			selectedFrom: [
				arrayIsEmpty(values.selectedFrom, 'This field is required')
			],
			selectedTo: [
				arrayIsEmpty(values.selectedTo, 'This field is required')
			],
		};

		const getError = (validation: Array<string | undefined>) => {
			const foundError = validation.find(item => item !== undefined) || null;
			if (foundError) {
				hasErrors = true;
			}
			
			return foundError || null;
		};

		return {
			hasErrors: () => hasErrors,
			errors: {
				from: getError(validations.selectedFrom),
				to: getError(validations.selectedTo)
			}
		};
	}, []);

	const getValues = useCallback((data: WorkItemTransitionTriggerType) => {
		return {
			fromValue: data.selectedFrom,
			toValue: data.selectedTo
		};
	}, []);

	const loadValues = useCallback(async (input: {
		organizationName: string;
		projectId: string;
		data: WorkItemTransitionsInputDataType;
	}): Promise<WorkItemTransitionTriggerType> => {
		const options = await getTransitionOptions({
			organizationName: input.organizationName,
			projectId: input.projectId,
		});

		return {
			selectedFrom: options.filter(item => input.data.fromValue.includes(item.value)).map(item => item.value),
			selectedTo: options.filter(item => input.data.toValue.includes(item.value)).map(item => item.value),
			loading: false
		};
	}, []);

	return {
		getTransitionOptions,
		validate,
		getValues,
		loadValues
	};
}